import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Search, Loader2 } from "lucide-react";

import { API_BASE_URL } from "../../config";
import { apiFetch } from "../../utils/api";
import { fmtCurrency } from "../../utils/format";

const normalizeOffer = (offer) => {
  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const normalizeProductDetails = (details) => {
    if (!details || typeof details !== "object") return null;
    return {
      ...details,
      net_price: toNumber(details.net_price),
      vat_value: toNumber(details.vat_value),
      gross_price: toNumber(details.gross_price),
      vat_rate: toNumber(details.vat_rate),
    };
  };
  return {
    ...offer,
    current_price: toNumber(offer.current_price),
    available: Number.isFinite(Number(offer.available)) ? Number(offer.available) : null,
    product_details: normalizeProductDetails(offer.product_details),
  };
};

const formatPrice = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return fmtCurrency(numeric);
};

export const LinkOffersModal = ({ product, authToken, authFetch, onClose, onComplete }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const initialSelectionIds = useMemo(
    () => (product?.linked_offers ?? []).map((offer) => offer.id),
    [product],
  );

  const initialSelectionSet = useMemo(
    () => new Set(initialSelectionIds),
    [initialSelectionIds],
  );

  useEffect(() => {
    setSelectedIds(new Set(initialSelectionIds));
  }, [initialSelectionIds]);

  useEffect(() => {
    if (!authToken || !product) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE_URL}/api/offers/`, { token: authToken })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setOffers(list.map(normalizeOffer));
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError(fetchError.message || "Failed to load offers.");
        setOffers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authToken, product]);

  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const filteredOffers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return offers;
    return offers.filter((offer) => {
      const haystack = [
        offer.name,
        offer.offer_id,
        offer.sku,
        offer.product_details?.symbol,
        offer.product_details?.ean,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [offers, searchTerm]);

  const hasChanges = useMemo(() => {
    if (initialSelectionSet.size !== selectedIds.size) return true;
    for (const id of selectedIds) {
      if (!initialSelectionSet.has(id)) {
        return true;
      }
    }
    return false;
  }, [initialSelectionSet, selectedIds]);

  const toggleSelection = useCallback((offerId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  }, []);

  const persistOfferLink = useCallback(
    async (offerId, payload) => {
      if (!authFetch) {
        throw new Error("Authentication required.");
      }
      const response = await authFetch(`${API_BASE_URL}/api/offers/${offerId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data?.detail || data?.message || `HTTP ${response.status}`;
        throw new Error(detail);
      }
      return data;
    },
    [authFetch],
  );

  const handleSave = useCallback(async () => {
    if (!product) return;
    if (!hasChanges) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    const toLink = [...selectedIds].filter((id) => !initialSelectionSet.has(id));
    const toUnlink = [...initialSelectionSet].filter((id) => !selectedIds.has(id));
    try {
      const tasks = [];
      toLink.forEach((offerId) => tasks.push(persistOfferLink(offerId, { product: product.id })));
      toUnlink.forEach((offerId) => tasks.push(persistOfferLink(offerId, { product: null })));
      if (tasks.length) {
        await Promise.all(tasks);
      }
      if (onComplete) {
        await onComplete();
      }
      onClose();
    } catch (persistError) {
      setError(persistError.message || "Failed to update offer links.");
    } finally {
      setSaving(false);
    }
  }, [hasChanges, initialSelectionSet, onClose, onComplete, persistOfferLink, product, selectedIds]);

  const handleBackdropClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget && !saving) {
        onClose();
      }
    },
    [onClose, saving],
  );

  if (!product) return null;

  const selectedCount = selectedIds.size;
  const selectionInfo =
    selectedCount === 0
      ? "No offers linked"
      : selectedCount === 1
        ? "1 offer linked"
        : `${selectedCount} offers linked`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm px-4"
      onMouseDown={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Link offers to {product.symbol || product.ean || "product"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Choose which Allegro offers should reuse this product&apos;s purchase data.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Product snapshot
            </div>
            <div className="mt-3 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">Symbol</div>
                <div className="font-semibold text-slate-900">{product.symbol || "—"}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">EAN</div>
                <div className="font-semibold text-slate-900">{product.ean || "—"}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">
                  Net purchase price
                </div>
                <div className="font-semibold text-slate-900">
                  {product.net_price != null ? fmtCurrency(product.net_price) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">
                  Gross purchase price
                </div>
                <div className="font-semibold text-slate-900">
                  {product.gross_price != null ? fmtCurrency(product.gross_price) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">VAT value</div>
                <div className="font-semibold text-slate-900">
                  {product.vat_value != null ? fmtCurrency(product.vat_value) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">VAT rate</div>
                <div className="font-semibold text-slate-900">
                  {product.vat_rate != null ? `${Number(product.vat_rate).toFixed(2)}%` : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 shadow-sm md:flex-none md:min-w-[280px]">
              <Search size={16} className="text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search offers by name, SKU or EAN..."
                className="w-full border-none bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
              />
            </label>
            <div className="text-sm font-medium text-slate-700">{selectionInfo}</div>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={16} className="animate-spin" />
                Loading offers...
              </div>
            ) : filteredOffers.length === 0 ? (
              <div className="text-sm text-slate-500">No offers match your search.</div>
            ) : (
              filteredOffers.map((offer) => {
                const isSelected = selectedIds.has(offer.id);
                const isLinkedElsewhere =
                  offer.product && offer.product !== product.id && offer.product_details;
                const isLinkedHere = offer.product && offer.product === product.id;
                return (
                  <label
                    key={offer.id}
                    className={`flex cursor-pointer gap-3 rounded-lg border px-3 py-3 text-sm shadow-sm transition ${
                      isSelected ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={isSelected}
                      onChange={() => toggleSelection(offer.id)}
                    />
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold text-slate-900">{offer.name || "—"}</div>
                        {isLinkedHere ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-emerald-800">
                            Linked here
                          </span>
                        ) : isLinkedElsewhere ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-amber-800">
                            Linked to other product
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-600">
                            Unlinked
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        Offer ID: {offer.offer_id} • SKU: {offer.sku || "—"} • Price:{" "}
                        {formatPrice(offer.current_price)}
                      </div>
                      <div className="text-xs text-slate-500">
                        Product data:&nbsp;
                        {offer.product_details ? (
                          <>
                            {offer.product_details.symbol || offer.product_details.ean || "—"} • Net{" "}
                            {formatPrice(offer.product_details.net_price)} • Gross{" "}
                            {formatPrice(offer.product_details.gross_price)}
                          </>
                        ) : (
                          "No product linked"
                        )}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <div className="text-xs text-slate-500">
            Linked offers will automatically read this product&apos;s EAN and purchase prices.
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading || !hasChanges}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                "Save links"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkOffersModal;
